import React, { useState } from 'react';
import styles from './OrderDetailsModal.module.css';

const OrderDetailsModal = ({ order, onClose, onUpdateStatus }) => {
  const [selectedStatus, setSelectedStatus] = useState(order.status);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editedProducts, setEditedProducts] = useState(
    Array.isArray(order.products) ? order.products : []
  );

  const statusOptions = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'en_proceso', label: 'En Proceso' },
    { value: 'enviada', label: 'Enviada' },
    { value: 'entregada', label: 'Entregada' },
    { value: 'cancelada', label: 'Cancelada' }
  ];

  const handleUpdateStatus = async () => {
    if (selectedStatus === order.status) return;

    setIsUpdating(true);
    try {
      await onUpdateStatus(order.id, selectedStatus);
      onClose();
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Error al actualizar el estado de la orden');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleQuantityChange = (index, newQuantity) => {
    const quantity = parseInt(newQuantity, 10);
    if (isNaN(quantity) || quantity < 1) return;

    const updated = [...editedProducts];
    updated[index].quantity = quantity;
    setEditedProducts(updated);
  };

  const calculateTotal = () => {
    return editedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
  };

  // Parse products from JSONB
  const products = editedProducts;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Detalles de la Orden</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.scrollableContent}>
            {/* Información del Cliente */}
            <section className={styles.section}>
              <h3>Información del Cliente</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Nombre:</span>
                  <span className={styles.value}>{order.user_name}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Correo:</span>
                  <span className={styles.value}>{order.user_email}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Teléfono:</span>
                  <span className={styles.value}>{order.user_phone}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>Dirección:</span>
                  <span className={styles.value}>{order.user_address}</span>
                </div>
              </div>
            </section>

            {/* Productos */}
            <section className={styles.section}>
              <h3>Productos</h3>
              <div className={styles.productsTable}>
                <div className={styles.tableHeader}>
                  <div>Producto</div>
                  <div>Variantes</div>
                  <div>Cantidad</div>
                  <div>Precio Unit.</div>
                  <div>Subtotal</div>
                </div>
                {products.map((product, index) => (
                  <div key={index} className={styles.tableRow}>
                    <div>{product.name}</div>
                    <div className={styles.variantsCell}>
                      {product.selected_attributes && Object.keys(product.selected_attributes).length > 0 ? (
                        <ul className={styles.attributeList}>
                          {Object.entries(product.selected_attributes).map(([key, value]) => (
                            <li key={key}><small>{key}: <strong>{value}</strong></small></li>
                          ))}
                        </ul>
                      ) : (
                        <span className={styles.noVariants}>Sin variantes</span>
                      )}
                    </div>
                    <div>
                      <input
                        type="number"
                        min="1"
                        value={product.quantity}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        className={styles.quantityInput}
                      />
                    </div>
                    <div>${product.price?.toFixed(2)}</div>
                    <div>${(product.price * product.quantity)?.toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Total:</span>
                <span className={styles.totalValue}>${calculateTotal().toFixed(2)}</span>
              </div>
            </section>

            {/* Estado de la Orden */}
            <section className={styles.section}>
              <h3>Estado de la Orden</h3>
              <div className={styles.statusControl}>
                <div className={styles.statusInfo}>
                  <span className={styles.label}>Estado Actual:</span>
                  <span className={`${styles.statusBadge} ${styles[order.status]}`}>
                    {statusOptions.find(s => s.value === order.status)?.label || order.status}
                  </span>
                </div>
                <div className={styles.statusInfo}>
                  <span className={styles.label}>Fecha de Creación:</span>
                  <span className={styles.value}>
                    {new Date(order.created_at).toLocaleString('es-SV', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>

              <div className={styles.statusUpdate}>
                <label htmlFor="statusSelect">Cambiar Estado:</label>
                <select
                  id="statusSelect"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className={styles.statusSelect}
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </section>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isUpdating}
          >
            Cancelar
          </button>
          <button
            className={styles.saveButton}
            onClick={handleUpdateStatus}
            disabled={isUpdating || selectedStatus === order.status}
          >
            {isUpdating ? 'Actualizando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
